/**
 * WEB COPY of serverSession.ts (adds the cookie handling).
 */
import { API_URL } from '../../config';
import { createLogger } from '../../utils/logger';
import { getAuthToken } from './tokenStorage';

const logger = createLogger('serverSession.web');

/**
 * Tells the server this session is over, so the access token stops working
 * there too (before 2026-10-02 it stayed valid until it expired).
 * On the web it also clears the httpOnly "remember this computer" cookie,
 * which a page script cannot delete; `keepDeviceTrust` (the idle lock)
 * leaves that cookie in place.
 * Call it while the token is still stored. Best effort and time-limited:
 * a failure must never block the local wipe that follows.
 */
export type ServerSessionEnd = 'ended' | 'no-session' | 'not-confirmed';

async function attempt(token: string | null, keepDeviceTrust: boolean): Promise<ServerSessionEnd> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ keepDeviceTrust }),
      signal: controller.signal,
    });
    if (!response.ok) return 'not-confirmed';
    const body = await response.json().catch(() => null);
    // The server says whether it recorded the sign-out: true, false (it
    // could not), or null (it was given no usable token).
    if (body?.sessionRevoked === true) return 'ended';
    // An explicit null is the server saying no usable token was presented.
    // Anything else (false, a missing field, an unreadable body) is not a
    // confirmation when a token was sent.
    if (body && body.sessionRevoked === null) return 'no-session';
    return token ? 'not-confirmed' : 'no-session';
  } catch (err) {
    logger.warn('Could not end the server session', err);
    return 'not-confirmed';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 'ended': the server now refuses this session's token. 'no-session':
 * there was no live token to end (already expired). 'not-confirmed': the
 * server could not be reached or could not record it, after one retry;
 * the caller still wipes locally but must not assume the token is dead.
 */
export async function endServerSession(options: { keepDeviceTrust?: boolean } = {}): Promise<ServerSessionEnd> {
  const token = await getAuthToken().catch(() => null);
  const keep = !!options.keepDeviceTrust;
  const first = await attempt(token, keep);
  if (first !== 'not-confirmed') return first;
  return attempt(token, keep);
}
